import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { resizeImage } from '../utils/imageUtils';
import ReactMarkdown from 'react-markdown';

import PersonaImage from '../components/molecules/PersonaImage';
import ConfigPanel from '../components/chat/ConfigPanel';
import SuggestionGrid from '../components/chat/SuggestionGrid';
import CreativeStudioModal from '../components/studio/CreativeStudioModal';
import {
  postPrivateChatCompletion,
  postPublicChatCompletion,
  getPresignedUploadUrl,
} from '../services/apiService';
import { promptSuggestions } from '../content/personaContent';
import { useUserStatus } from '../context/UserStatusContext';
import styles from './ChatPage.module.css';

const STATIC_TEXT_COST = 1;
const STATIC_IMAGE_COST = 2;

function ChatPage({
  mode = 'private',
  publicPersonaData = null,
  view = 'full',
}) {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { userStatus, refreshUserStatus } = useUserStatus();
  const navigate = useNavigate();
  const { personaId } = useParams();
  const location = useLocation();
  const hasShownSuggestion = useRef(false);

  const isOwner =
    location.state?.isOwner ?? (mode === 'private' && isAuthenticated);

  const [persona, setPersona] = useState(
    mode === 'public'
      ? publicPersonaData
      : location.state?.persona || { personaName: 'Loading...' }
  );
  const [messages, setMessages] = useState(() => {
    if (mode === 'public' && publicPersonaData?.welcomeMessage) {
      return [{ role: 'assistant', content: publicPersonaData.welcomeMessage }];
    }
    return [];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState({
    tone: 'neutral',
    emotionalState: '',
    responseConstraints: [],
    verbosity: 'normal',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isStudioOpen, setIsStudioOpen] = useState(false);
  const [studioInitialData, setStudioInitialData] = useState({});

  const [copyButtonText, setCopyButtonText] = useState('Copy Chat');
  const [isDepleted, setIsDepleted] = useState(false);
  const chatWindowRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (mode === 'public' && publicPersonaData) {
      setPersona(publicPersonaData);

      if (publicPersonaData.welcomeMessage) {
        setMessages([
          { role: 'assistant', content: publicPersonaData.welcomeMessage },
        ]);
      } else {
        setMessages([]);
      }
    }
  }, [publicPersonaData, mode]);

  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    return () => {
      messages.forEach((msg) => {
        if (msg.imagePreview && msg.imagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(msg.imagePreview);
        }
      });
    };
  }, [messages]);

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const resizedFile = await resizeImage(file);

        setImageFile(resizedFile);
        setImagePreview(URL.createObjectURL(resizedFile));
      } catch (error) {
        console.error('Image resizing failed:', error);
        alert(
          'There was an issue processing your image. Please try a different one.'
        );
      }
    }
    e.target.value = null;
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const submitMessage = async (messageContent) => {
    const content = messageContent.trim();
    if (!content && !imageFile) return;

    setIsLoading(true);
    const userMessage = { role: 'user', content, imagePreview };
    setMessages((prev) => [...prev, userMessage]);

    const fileToUpload = imageFile;
    setInput('');
    clearImage();

    try {
      const chatTargetId = mode === 'public' ? persona?.personaId : personaId;

      // --- PUBLIC CHAT LOGIC ---
      if (mode === 'public') {
        const payload = {
          personaId: chatTargetId,
          messages: [...messages, userMessage],
          verbosity: config.verbosity,
        };
        const data = await postPublicChatCompletion(payload);
        const assistantMessage = { role: 'assistant', content: data.response };
        setMessages((prev) => [...prev, assistantMessage]);

        // --- PRIVATE CHAT LOGIC ---
      } else {
        const accessToken = await getAccessTokenSilently();

        // --- Multimodal Chat (Text + Image) ---
        if (fileToUpload) {
          if (!userStatus || userStatus.planTier === 'free') {
            throw new Error(
              'Image analysis is a Premium/Pro feature. Please upgrade your plan.'
            );
          }

          const presignedData = await getPresignedUploadUrl(
            {
              file_type: fileToUpload.type,
              file_extension: fileToUpload.name.split('.').pop(),
            },
            accessToken
          );

          const formData = new FormData();
          Object.entries(presignedData.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', fileToUpload);
          await fetch(presignedData.url, { method: 'POST', body: formData });

          const payload = {
            personaId,
            messages: [...messages, userMessage],
            image_url: presignedData.public_url,
            ...config,
          };
          const data = await postPrivateChatCompletion(payload, accessToken);
          const assistantMessage = {
            role: 'assistant',
            content: data.response,
          };
          setMessages((prev) => [...prev, assistantMessage]);

          // --- Standard Text Chat ---
        } else {
          const payload = {
            personaId,
            messages: [...messages, userMessage],
            ...config,
          };
          const data = await postPrivateChatCompletion(payload, accessToken);
          const assistantMessage = {
            role: 'assistant',
            content: data.response,
          };

          const isPro = userStatus?.planTier !== 'free';
          if (
            isPro &&
            data.response.length > 150 &&
            !hasShownSuggestion.current
          ) {
            const suggestionMessage = {
              role: 'assistant',
              type: 'suggestion',
              content:
                'This looks like a great piece of content! Posts with an image often see higher engagement.',
              prompt: 'Create a complementary image for this post? (Costs 1🎨)',
              sourceText: data.response,
            };
            setMessages((prev) => [
              ...prev,
              assistantMessage,
              //suggestionMessage,
            ]);
            hasShownSuggestion.current = true;
          } else {
            setMessages((prev) => [...prev, assistantMessage]);
          }
        }
        await refreshUserStatus();
      }
    } catch (error) {
      const errorMessage = {
        role: 'system',
        content: `Error: ${error.message}`,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionAccept = (sourceText) => {
    setStudioInitialData({ prompt: sourceText, outputFormat: 'image' });
    setIsStudioOpen(true);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    submitMessage(input);
  };
  const handleSuggestionClick = (suggestion) => {
    submitMessage(suggestion);
  };
  const handleCopyChat = () => {
    const header = `--- Ego Conversation with: ${persona?.personaName} ---`;
    const formattedMessages = messages
      .map(
        (msg) =>
          `${msg.role === 'user' ? 'Me' : persona?.personaName}: ${msg.content}`
      )
      .join('\n\n');
    navigator.clipboard
      .writeText(`${header}\n${formattedMessages}`)
      .then(() => {
        setCopyButtonText('Copied!');
        setTimeout(() => setCopyButtonText('Copy Chat'), 2000);
      });
  };

  const suggestions =
    persona?.promptSuggestions && persona.promptSuggestions.length > 0
      ? persona.promptSuggestions
      : promptSuggestions[persona?.type] || promptSuggestions['default'];

  const getSendButtonContent = () => {
    if (mode === 'public') return 'Send';
    if (imageFile) return `Send | ${STATIC_IMAGE_COST}⚡️`;
    return `Send | ${STATIC_TEXT_COST}⚡️`;
  };

  const displayName = persona?.profileName || persona?.personaName;

  const isConversationStart =
    (mode === 'public' && messages.length <= 1) ||
    (mode === 'private' && messages.length === 0);

  return (
    <div
      className={
        view === 'embedded' ? styles.embeddedContainer : styles.pageContainer
      }
    >
      {view === 'embedded' ? (
        <div className={styles.embeddedHeader}>
          <PersonaImage
            personaId={persona.personaId}
            username={persona.username}
            alt={displayName}
            className={styles.embeddedAvatar}
            isOwner={false}
          />
          <h4>{displayName}</h4>
        </div>
      ) : (
        mode === 'private' && (
          <div className={`page-header ${styles.playgroundHeader}`}>
            <div className={styles.headerContent}>
              <PersonaImage
                personaId={personaId}
                alt={persona?.personaName}
                className={styles.headerAvatar}
                isOwner={isOwner}
              />
              <div>
                <h2>
                  {isOwner ? 'Test Persona:' : 'Chat with:'} {displayName}
                </h2>
                <span className={styles.personaIdDisplay}>ID: {personaId}</span>
              </div>
            </div>
            <div className={styles.headerActions}>
              {messages.length > 0 && (
                <button
                  onClick={handleCopyChat}
                  className="action-button secondary"
                >
                  <i className="fas fa-copy" style={{ marginRight: '8px' }}></i>
                  {copyButtonText}
                </button>
              )}
              <button
                onClick={() => navigate(-1)}
                className="action-button secondary"
              >
                <i
                  className="fas fa-arrow-left"
                  style={{ marginRight: '8px' }}
                ></i>
                Back
              </button>
            </div>
          </div>
        )
      )}

      <div className={styles.chatLayout}>
        <div className={styles.chatWidget}>
          <div className={styles.chatWindow} ref={chatWindowRef}>
            {messages.length === 0 && !isLoading && !isDepleted && (
              <div className={styles.emptyChatContainer}>
                <p className={`${styles.chatMessage} ${styles.system}`}>
                  Start the conversation.
                </p>
                {mode !== 'public' && (
                  <p className={styles.costInfo}>
                    <i className="fas fa-info-circle"></i>
                    <span>
                      {' '}
                      Messages cost {STATIC_TEXT_COST}⚡️. Attaching an image
                      costs an additional 1⚡️ ({STATIC_IMAGE_COST}⚡️ total).
                    </span>
                  </p>
                )}
              </div>
            )}
            {isDepleted && (
              <div className={`${styles.chatMessage} ${styles.system}`}>
                🔥 This Profile is in High Demand! The creator's monthly
                engagement quota has been reached. Please check back again soon.
              </div>
            )}
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`${styles.chatMessage} ${styles[msg.role]}`}
              >
                {msg.imagePreview && (
                  <img
                    src={msg.imagePreview}
                    alt="User upload preview"
                    className={styles.generatedImage}
                  />
                )}
                {msg.imageUrl && (
                  <img
                    src={msg.imageUrl}
                    alt={msg.content}
                    className={styles.generatedImage}
                  />
                )}

                {msg.content && <ReactMarkdown>{msg.content}</ReactMarkdown>}

                {msg.enhancedPrompt && (
                  <p className={styles.directorPrompt}>
                    <strong>Art Director's Prompt:</strong> {msg.enhancedPrompt}
                  </p>
                )}
              </div>
            ))}

            {isLoading && (
              <div className={`${styles.chatMessage} ${styles.assistant}`}>
                <div className={styles.typingIndicator}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            {isConversationStart && !isLoading && (
              <SuggestionGrid
                suggestions={suggestions}
                onSuggestionClick={handleSuggestionClick}
              />
            )}
          </div>

          <form className={styles.chatInputForm} onSubmit={handleSendMessage}>
            {imagePreview && (
              <div className={styles.imagePreviewContainer}>
                <img src={imagePreview} alt="Selected preview" />
                <button type="button" onClick={clearImage}>
                  &times;
                </button>
              </div>
            )}
            <div className={styles.inputRow}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />

              {mode === 'private' && (
                <>
                  <button
                    type="button"
                    className={styles.studioButton}
                    onClick={() => setIsStudioOpen(true)}
                    title="Open Creative Studio"
                  >
                    <i className="fas fa-magic-wand-sparkles"></i>
                  </button>
                  <button
                    type="button"
                    className={styles.attachButton}
                    onClick={() => fileInputRef.current.click()}
                    title="Attach an Image"
                    disabled={
                      isLoading || isDepleted || userStatus?.planTier === 'free'
                    }
                  >
                    <i className="fas fa-paperclip"></i>
                  </button>
                </>
              )}

              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`Message ${displayName || ''}...`}
                disabled={isLoading || isDepleted}
              />
              <button
                type="submit"
                className="action-button primary"
                disabled={isLoading || isDepleted}
              >
                {getSendButtonContent()}
              </button>
            </div>
          </form>
        </div>
        {isOwner && <ConfigPanel config={config} setConfig={setConfig} />}
      </div>

      {isStudioOpen && (
        <CreativeStudioModal
          onClose={(result) => {
            setIsStudioOpen(false);
            if (result) {
              if (result.textResponse) {
                const textMessage = {
                  role: 'assistant',
                  content: result.textResponse,
                };
                setMessages((prev) => [...prev, textMessage]);
              }
              if (result.imageUrl) {
                const imageMessage = {
                  role: 'assistant',
                  content: result.imageDescription,
                  imageUrl: result.imageUrl,
                  enhancedPrompt: result.enhancedPrompt,
                };
                setMessages((prev) => [...prev, imageMessage]);
              }
            }
          }}
          persona={persona}
          initialData={studioInitialData}
        />
      )}
    </div>
  );
}

const AuthenticatedChatPage = withAuthenticationRequired(ChatPage);
export { AuthenticatedChatPage };
export default ChatPage;
